import React, { useEffect, useState } from "react";
import { uniqueId } from "lodash-es";
import SurveyInput from "../../Components/SurveyInput/SurveyInput";
import LocalizedText from "../../../presentational/LocalizedText";
import { SurveyQuestionProps } from "../../Components/SurveyQuestion/SurveyQuestion";
import { NumericAnswerFormat } from "../../../../types/Survey";
import SurveyFormItem from "../../Components/SurveyFormItem/SurveyFormItem";
import useTranslate from "../../../../hooks/useTranslate";
import { QuestionResultModel } from "../../../../types/SurveyResult";

export default function (props: SurveyQuestionProps) {
    let [validationError, setValidationError] = useState<string | undefined>(undefined);
    let defaultPlaceholder = useTranslate(["default-numeric-placeholder"])["default-numeric-placeholder"];
    let currentResult = props.result as QuestionResultModel;
    const currentAnswer = currentResult.Answers[0] || "";

    let answerFormat = props.answerFormat as NumericAnswerFormat;

    type NumericValidationError = 'numeric-validation-error-min' | 'numeric-validation-error-max';

    function answerChanged(answer: string) {
        //prevent typing non-numeric characters
        if (!answerMatchesStyle(answer)) {
            return;
        }

        //clear validation errors immediately when the answer is valid
        let validationError = getValidationError(answer);
        if (validationError === undefined) {
            setValidationError(undefined);
        }
        props.onResultChange({ ...props.result, Answers: [answer], invalid: !!validationError } as QuestionResultModel);
    }

    function getValidationError(answer: string): NumericValidationError | undefined {
        if (props.optional && answer === "") {
            return undefined;
        }

        let answerNumber = parseFloat(answer);
        if (answerFormat.minimum !== undefined && answerFormat.minimum !== null && answerNumber < answerFormat.minimum) {
            return 'numeric-validation-error-min';
        }
        else if (answerFormat.maximum !== undefined && answerFormat.maximum !== null && answerNumber > answerFormat.maximum) {
            return 'numeric-validation-error-max';
        }

        return undefined;
    }

    function answerMatchesStyle(answer: string) {
        if (answerFormat.style === "Integer") {
            return /^-?[0-9]*$/.test(answer);
        } else if (answerFormat.style === "Decimal") {
            if (!/^-?[0-9]*\.?[0-9]*$/.test(answer)) {
                return false;
            }

            const max = answerFormat.maxDecimalPlaces;
            if (max == null) {
                return true;
            }

            const decimalIndex = answer.indexOf('.');
            if (decimalIndex !== -1) {
                const decimalPart = answer.substring(decimalIndex + 1);
                if (decimalPart.length > max) {
                    return false;
                }
            }

            return true;
        }
    }

    useEffect(() => {
        // set initial validation error if the answer is not valid
        if (currentAnswer) {
            answerChanged(currentAnswer);
            setValidationError(getValidationError(currentAnswer))
        }
    }, []);

    return <SurveyFormItem
        topBorder
        bottomBorder
        label={props.label}
        labelFor={props.inputIdForLabel}
        validationError={validationError ? <LocalizedText textKey={validationError} values={
            {
                currentValue: currentAnswer,
                minimumValue: answerFormat.minimum,
                maximumValue: answerFormat.maximum
            }
        } /> : undefined}
        labelPosition={props.labelPosition}
        variant={props.variant}
        useFieldsetFormat={props.useFieldsetFormat}
        index={props.index}>
        <SurveyInput id={props.inputIdForLabel}
            aria-describedby={validationError ? `${props.inputIdForLabel + "-error"}` : ''}
            value={currentAnswer}
            type="text"
            placeholder={props.placeholder || defaultPlaceholder}
            onChange={(e) => answerChanged(e.target.value)}
            onEnterPressed={props.onEnterPressed}
            inputMode="decimal"
            onBlur={(e) => setValidationError(getValidationError(e.target.value))}
            variant={props.variant} />
    </SurveyFormItem>
}
